import { LitElement, html, css, nothing, type PropertyValues } from 'lit'
import { consume } from '@lit/context'
import { customElement, property, state } from 'lit/decorators.js'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT, getBreakpoint } from '../../util/breakpoints'
import { withBase } from '../../util/base-path'
import { QuoteService } from '../../services/quote-service'
import { decryptAndParseJson } from '../../util/crypto'
import {
  emailHeaderTemplate,
  emailFooterTemplate,
} from './producer-email-entry-templates.js'

interface Notice {
  ccAgent: boolean
  message: string
}

@customElement('rq-producer-email-entry')
export class ProducerEmailEntry extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .result-error-box {
      color: var(--lh-color-danger-600, #b00020);
      margin-bottom: 12px;
    }
    .email-from-name,
    .email-to-name,
    .email-title-name {
      margin: 4px 0;
    }
    textarea.message {
      width: 100%;
      min-height: 140px;
      border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
      border-radius: var(--lh-radii-sm, 8px);
      padding: 10px;
      font-family: inherit;
      font-size: 14px;
    }
    .dashed-box {
      background: var(--lh-color-neutral-white);
      padding: 32px;
      border-radius: var(--lh-radii-sm, 8px);
    }
    .agent-note-textarea {
      --Form-input-background-color: var(--lh-color-neutral-white, #ffffff);
      --Form-textarea-background-color: var(--lh-color-neutral-white, #ffffff);
    }
    .loader-slot {
      display: inline-flex;
      width: 28px;
      justify-content: center;
      align-items: center;
    }
    .meta {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
    .producer-email-link {
      max-width: 100%;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    @media (max-width: 767px) {
      .producer-email-link {
        display: block;
      }
    }
  `

  @consume({ context: shopperContext, subscribe: true })
  shopperContextValue?: ShopperContextType

  @consume({ context: breakpointContext, subscribe: true })
  breakpoint!: BREAKPOINT

  @state() private viewportWidth =
    typeof window === 'undefined' ? 1025 : window.innerWidth

  private onResize = () => {
    const width = window.innerWidth
    if (this.viewportWidth !== width) {
      this.viewportWidth = width
    }
  }

  private textareaBackgroundRetryId: number | null = null

  connectedCallback(): void {
    super.connectedCallback()
    if (typeof window !== 'undefined') {
      this.viewportWidth = window.innerWidth
      window.addEventListener('resize', this.onResize)
    }
  }

  disconnectedCallback(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
      if (this.textareaBackgroundRetryId !== null) {
        window.cancelAnimationFrame(this.textareaBackgroundRetryId)
        this.textareaBackgroundRetryId = null
      }
    }
    super.disconnectedCallback()
  }

  get isMobile() {
    const resolvedBreakpoint =
      this.breakpoint || getBreakpoint(this.viewportWidth)
    return (
      resolvedBreakpoint === BREAKPOINT.PHONE ||
      resolvedBreakpoint === BREAKPOINT.TABLET
    )
  }

  @property({ type: String }) sendMailDone: 'n' | 'y' = 'n'
  @property({ type: Object }) notice: Notice = { ccAgent: false, message: '' }
  @property({ type: String }) errorHtml: string = ''
  @property({ type: String }) planYear: string = ''
  @property({ type: String }) baseUrl: string = ''
  @property({ type: String }) brochureLink: string = ''
  @property({ type: Boolean }) loading: boolean = false
  @property({ type: Boolean }) productionRegion: boolean = true

  @state() private messageLen: number = 0

  private get individualAgencyName(): string {
    try {
      const forms = QuoteService.getCachedAgentForms()
      const pinfo = forms?.producerInfoJ
      if (pinfo) {
        const producer = decryptAndParseJson(pinfo)
        return producer?.individualAgencyName || ''
      }
    } catch {
      // non-blocking
    }
    return ''
  }

  private onCancel() {
    this.dispatchEvent(new CustomEvent('cancel'))
  }

  private onSend() {
    this.dispatchEvent(
      new CustomEvent('send', { detail: { notice: this.notice } })
    )
  }

  private onDashboard() {
    this.dispatchEvent(new CustomEvent('dashboard'))
  }

  private onCcAgentToggle(e: Event) {
    const target = e.target as HTMLInputElement
    this.notice = { ...this.notice, ccAgent: !!target.checked }
  }

  private onMessageInput(e: Event) {
    const target = e.target as HTMLTextAreaElement
    const msg = target.value || ''
    this.messageLen = msg.length
    this.notice = { ...this.notice, message: msg }
  }

  private applyAgentNoteTextareaBackground(): void {
    const textareaHost = this.renderRoot.querySelector(
      'lh-textarea.agent-note-textarea'
    ) as (HTMLElement & { shadowRoot: ShadowRoot | null }) | null

    if (!textareaHost?.shadowRoot) {
      return
    }

    const shadow = textareaHost.shadowRoot
    const control = shadow.querySelector(
      '.lh-textarea__control'
    ) as HTMLElement | null
    const nativeTextarea = shadow.querySelector(
      'textarea'
    ) as HTMLTextAreaElement | null

    if (control) {
      control.style.setProperty('background-color', '#ffffff', 'important')
      control.style.setProperty('background', '#ffffff', 'important')
    }

    if (nativeTextarea) {
      nativeTextarea.style.setProperty(
        'background-color',
        '#ffffff',
        'important'
      )
      nativeTextarea.style.setProperty('background', '#ffffff', 'important')
    }
  }

  private scheduleAgentNoteTextareaBackgroundApply(attempt = 0): void {
    if (typeof window === 'undefined') {
      return
    }

    if (attempt === 0 && this.textareaBackgroundRetryId !== null) {
      window.cancelAnimationFrame(this.textareaBackgroundRetryId)
      this.textareaBackgroundRetryId = null
    }

    this.applyAgentNoteTextareaBackground()

    if (attempt >= 5) {
      this.textareaBackgroundRetryId = null
      return
    }

    this.textareaBackgroundRetryId = window.requestAnimationFrame(() => {
      this.scheduleAgentNoteTextareaBackgroundApply(attempt + 1)
    })
  }

  protected firstUpdated(changedProperties: PropertyValues<this>): void {
    super.firstUpdated(changedProperties)
    this.scheduleAgentNoteTextareaBackgroundApply()
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    super.updated(changedProperties)
    this.scheduleAgentNoteTextareaBackgroundApply()
  }

  private getDisabledSend(): boolean {
    return this.messageLen > 1000
  }

  private renderForm() {
    return html`
      <lh-container
        theme="transparent"
        content-width="lg"
        gutters="none"
        spacing="tight"
        context-id="producer-email-entry-form"
      >
        <lh-container gutters="lg" theme="transparent" margin-bottom="lg">
          <div class="result-error-box" .innerHTML=${this.errorHtml}></div>
          <lh-typography type="heading" size=${this.isMobile ? 'xl' : 'xl'}
            >Rate quote notice</lh-typography
          >
          <lh-typography color="subtle">
            The notification displayed below will be e-mailed to your customer
            to inform them a rate quote is available for review. You can enter
            optional comments in the text box provided.
            <strong
              >The “View your quote” button will be enabled for the
              customer.</strong
            >
          </lh-typography>
          <lh-typography>
            <lh-checkbox
              name="copy-of-quote"
              value="vision"
              ?default-checked=${this.notice.ccAgent}
              @lh-change=${this.onCcAgentToggle}
            >
              <lh-typography color="subtle"
                >Check this box if you'd like to send a copy of this quote to
                yourself.</lh-typography
              >
            </lh-checkbox>
          </lh-typography>
          <lh-typography color="subtle">
            Your e-mail address has been included in the e-mail to remind your
            customer to contact you directly. If your customer replies to the
            system generated e-mail, the reply will not reach you.
            <strong>
              Selecting the Cancel button will delete the e-mail and a draft
              will not be saved.
            </strong>
          </lh-typography>
        </lh-container>

        <div class="dashed-box">
          <lh-typography class="email-from-name" color="subtle"
            >From:
            ${this.shopperContextValue?.producerSession?.firstName &&
            this.shopperContextValue?.producerSession?.lastName
              ? ` ${this.shopperContextValue.producerSession.firstName} ${this.shopperContextValue.producerSession.lastName}`
              : this.shopperContextValue?.producerSession?.producerName
                ? ` ${this.shopperContextValue.producerSession.producerName}`
                : ''}</lh-typography
          >
          <lh-typography class="email-to-name" color="subtle"
            >To: ${this.shopperContextValue?.formData?.firstName}
            ${this.shopperContextValue?.formData?.lastName}</lh-typography
          >
          <lh-typography color="subtle"
            >Subject: Rate quote for Blue Cross NC plan</lh-typography
          >
          <lh-divider spacing-top="md"></lh-divider>
          <lh-container gutters="none" margin-top="md" theme="transparent">
            ${emailHeaderTemplate}
            <lh-container
              gutters=${this.isMobile ? 'md' : 'xl'}
              margin-top="md"
              margin-bottom="xl"
              theme="transparent"
            >
              <lh-typography
                spacing-top="md"
                type="heading"
                size=${this.isMobile ? 'xl' : 'xl'}
              >
                Your Blue Cross NC rate quote
              </lh-typography>
              <lh-typography
                class="email-title-name"
                spacing-top="md"
                color="subtle"
                >Hello
                ${this.shopperContextValue?.formData?.firstName},</lh-typography
              >
              <lh-typography spacing-top="md" color="subtle">
                Your Blue Cross and Blue Shield of North Carolina rate quote is
                ready for you to review.
              </lh-typography>
              <lh-typography spacing-top="md" color="subtle">
                If you have any questions about the rate quote or plan benefits,
                contact me at
                ${this.shopperContextValue?.producerSession?.email}.
                <strong
                  >Don’t reply directly to this email, as I won’t receive the
                  reply.</strong
                >
              </lh-typography>
              <lh-button variant="filled" size="lg" spacing-top="md"
                >View your quote</lh-button
              >
              <lh-typography spacing-top="md" size="lg" type="heading">
                When you’re ready to apply
              </lh-typography>
              <lh-typography spacing-top="md" color="subtle">
                You can set up a user ID and password to save your application
                progress and return anytime.
              </lh-typography>
              <lh-typography spacing-top="md" size="lg" type="heading">
                Additional information from your agent
              </lh-typography>
              <lh-typography spacing-top="md" color="subtle" size="sm">
                <i>Add any additional comments to the customer here.</i>
              </lh-typography>
              <lh-textarea
                class="agent-note-textarea"
                name="description"
                min-count="5"
                rows="5"
                help-text="Minimum of 5 characters"
                value=${this.notice.message}
                @lh-input=${this.onMessageInput}
                character-count="1000"
              >
              </lh-textarea>

              <!-- <lh-typography spacing-top="md" color="subtle"
                >View Your Rate Quote Here -
                <em
                  >Note: Hyperlink will be enabled in prospect's e-mail</em
                ></lh-typography
              >
              <lh-typography spacing-top="md" color="subtle">
                Once you review your quote, you can apply for coverage.
              </lh-typography> -->
              <lh-typography spacing-top="md" color="subtle"
                >Thank you for considering Blue Cross NC for your health care
                coverage.</lh-typography
              >
              <lh-typography
                class="agent-business-card"
                spacing-top="md"
                color="subtle"
              >
                Thanks,<br />
                ${this.shopperContextValue?.producerSession?.firstName &&
                this.shopperContextValue?.producerSession?.lastName
                  ? html`${this.shopperContextValue.producerSession.firstName}
                      ${this.shopperContextValue.producerSession.lastName}
                      <br />`
                  : this.shopperContextValue?.producerSession?.producerName
                    ? html`
                        ${this.shopperContextValue.producerSession.producerName}
                        <br />
                      `
                    : nothing}
                ${this.individualAgencyName
                  ? html`${this.individualAgencyName} <br />`
                  : nothing}
                ${this.shopperContextValue?.producerSession?.workPhoneNumber
                  ? html`${this.shopperContextValue?.producerSession
                        ?.workPhoneNumber} <br />`
                  : nothing}
                ${this.shopperContextValue?.producerSession?.email
                  ? html`<a
                        href="mailto:${this.shopperContextValue?.producerSession
                          ?.email}"
                        style="display: inline-block"
                        class="producer-email-link"
                        >${this.shopperContextValue?.producerSession?.email}</a
                      ><br />`
                  : nothing}
              </lh-typography>
            </lh-container>
          </lh-container>
          ${emailFooterTemplate(this.isMobile)}
          <lh-container
            gutters="none"
            margin-top="lg"
            content-width="fluid"
            alignment="center"
            spacing="none"
          >
            <div class="meta">
              <!-- rate-effective-through block intentionally hidden -->
              <div>
                <lh-button-group
                  orientation="horizontal"
                  size="lg"
                  alignment="right"
                >
                  <lh-button
                    variant="outlined"
                    @click=${this.onCancel}
                    analytics-id="ancillary-rate-quote:producer-email-entry:cancel-button:button-text"
                    >Cancel</lh-button
                  >
                  <lh-button
                    variant="filled"
                    ?disabled=${this.getDisabledSend()}
                    @click=${this.onSend}
                    analytics-id="ancillary-rate-quote:producer-email-entry:send-button:button-text"
                    >Send</lh-button
                  >
                </lh-button-group>
                <span class="loader-slot">
                  ${this.loading ? html`<lh-loader></lh-loader>` : nothing}
                </span>
              </div>
            </div>
          </lh-container>
        </div>
      </lh-container>
    `
  }

  private renderSent() {
    return html`
      <lh-container
        theme="white"
        content-width="fluid"
        gutters="none"
        spacing="tight"
        padding-top="lg"
        padding-bottom="lg"
        style="min-height: 100vh; width: 100%; display: flex; align-items: center; justify-content: center;"
      >
        <lh-container
          alignment="center"
          gutters="lg"
          spacing="xs"
          context-id="producer-email-entry-sent"
        >
          <lh-typography variant="h6"
            >Your e-mail notice was sent successfully.</lh-typography
          >
          <lh-button-group alignment="center" margin-top="lg">
            <lh-button
              variant="filled"
              href="${withBase('start-ancillary-quote') +
              window.location.search +
              window.location.hash}"
              analytics-id="ancillary-rate-quote:producer-email-entry:new-quote-button:button-text"
              >NEW QUOTE</lh-button
            >
            <lh-button
              variant="filled"
              @click=${this.onDashboard}
              analytics-id="ancillary-rate-quote:producer-email-entry:dashboard-button:button-text"
              >DASHBOARD</lh-button
            >
          </lh-button-group>
        </lh-container>
      </lh-container>
    `
  }

  render() {
    return html`
      ${this.sendMailDone === 'n' ? this.renderForm() : this.renderSent()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rq-producer-email-entry': ProducerEmailEntry
  }
}
